/**
 * Formatting helpers shared by the kit and the screens (re-exported from Ui.tsx).
 * Dates in the operator's locale, ids shortened in the middle, amounts with
 * grouped thousands and a real minus sign.
 */

/** «27.09.2026 21:36» — date and time in ru-RU; «—» for none. */
export function when(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return `${date.toLocaleDateString('ru-RU')} ${date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
}

/** Shortens an id/hash/address in the middle: head…tail; «—» for none. */
export function short(value: string | null | undefined, head = 8, tail = 6): string {
  if (!value) return '—'
  if (value.length <= head + tail + 1) return value
  return tail === 0 ? `${value.slice(0, head)}…` : `${value.slice(0, head)}…${value.slice(-tail)}`
}

/** An amount string in ru-RU with grouped thousands. `maxDecimals` caps the tail (a price needs 2, not the 8 a crypto amount may carry). */
export function money(value: string | null | undefined, decimals = 2, maxDecimals = 8): string {
  if (value === null || value === undefined) return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return value
  return n.toLocaleString('ru-RU', { minimumFractionDigits: decimals, maximumFractionDigits: Math.max(decimals, maxDecimals) }).replace(/\s/g, ' ').replace(/^-/, '−')
}

/** A decimal string or number as a number, or null when it is not one. */
export function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const trimmed = value.trim()
  if (!/^[-+]?\d+(\.\d+)?(e[-+]?\d+)?$/i.test(trimmed)) return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}

/** Digits after the point in a decimal string (capped), 0 for an integer number. */
export function decimalsOf(value: string | number | null | undefined, cap = 8): number {
  if (typeof value === 'string') {
    const dot = value.indexOf('.')
    return dot === -1 ? 0 : Math.min(cap, value.length - dot - 1)
  }
  if (typeof value === 'number' && !Number.isInteger(value)) return 2
  return 0
}

/** A number in ru-RU with exactly `decimals` digits, narrow spaces and a real minus. */
export function formatNumber(value: number, decimals = 0): string {
  return value.toLocaleString('ru-RU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).replace(/\s/g, '\u202f').replace(/^-/, '−')
}
